import { signal, WritableSignal } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TherapyJobForm } from '../models/TherapyJobForm';
import { TherapyJobMedicine } from '../models/generated/therapyJobMedicine';
import { PatientService } from '../services/patient.service';
import { PhysicianService } from '../services/physician.service';
import { DataTypeValidator } from '../utils/DataTypeValidator';
import { MedicineUnitValidator } from '../utils/MedicineUnitValidator';
import { DecimalPrecisionValidator } from '../utils/decimalPrecisionValidator';
import { getTimeFromDate } from '../utils/getTime';
import { PatientValidator } from '../utils/patientValidator';
import { PhysicianValidator } from '../utils/physicianValidator';
import { PositiveNumberValidator } from '../utils/positiveNumberValidator';

export const ABORT_REASON_MAX_LENGTH = 500;
export const ANNOTATION_MAX_LENGTH = 1000;

/** Sentinel `FinalizeReasonOptionKey.id` selecting the free-text "Others" reason. */
export const FINALIZE_REASON_OTHERS_ID = 'others';

export interface FinalizeReasonOptionKey {
  id: string;
  translationKey: string;
}

/** Selectable reasons for the force-finalization reason dropdown, in display order. */
export const FINALIZE_REASON_OPTION_KEYS: Array<FinalizeReasonOptionKey> = [
  { id: 'medicationAlreadyAdministered', translationKey: 'TherapyJobFinalizeReasonOptionMedicationAdministered' },
  { id: 'emergencySituation', translationKey: 'TherapyJobFinalizeReasonOptionEmergencySituation' },
  { id: 'defectiveDevice', translationKey: 'TherapyJobFinalizeReasonOptionDefectiveDevice' },
  { id: 'deviceCouldNotConnect', translationKey: 'TherapyJobFinalizeReasonOptionDeviceCouldNotConnect' },
  { id: 'batteryEmpty', translationKey: 'TherapyJobFinalizeReasonOptionBatteryEmpty' },
  { id: FINALIZE_REASON_OTHERS_ID, translationKey: 'TherapyJobFinalizeReasonOptionOthers' }
];

export const MAX_VALUE_FLOW_RATE = 999;
export const MAX_VALUE_VOLUME = 9999;
export const MAX_VALUE_DOSE = 9999;
export const MAX_DECIMALS_DOSE = 2;
export const MAX_DECIMALS_VOLUME = 2;
const START_DATE_CONTROL = 'startDate';
const DEFAULT_START_TIME_BUFFER_MS = 5 * 60 * 1000;

// hh:mm truncates seconds off "now", so a bare `new Date()` default can already read as
// in the past once recombined; 5 minutes of buffer also gives the user time to fill the form.
export const getDefaultStartDate = (): Date => new Date(Date.now() + DEFAULT_START_TIME_BUFFER_MS);

export class TherapyJobFormClass {
  private therapyJobForm: FormGroup<TherapyJobForm>;

  private readonly disabledFormSignal: WritableSignal<boolean> = signal(null);
  public disabledForm = this.disabledFormSignal.asReadonly();

  // Manual jobs have no HIS-originated timing to trust, so this is opted into only for manual
  // entry; PRN jobs may legitimately keep a start time in the past. Kept as a stable reference so
  // add/removeValidators target the same function.
  private readonly startTimeInFutureValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const startDateValue = control.parent?.get(START_DATE_CONTROL)?.value as Date;
    if (!control.value || !startDateValue) {
      return null;
    }

    const combined = new Date(startDateValue.getTime());
    const [hoursStr, minutesStr] = String(control.value).split(':');
    combined.setHours(Number(hoursStr), Number(minutesStr), 0, 0);

    return combined.getTime() > Date.now() ? null : { startTimeNotInFuture: true };
  };

  constructor(
    private readonly patientService?: PatientService,
    private readonly physicianService?: PhysicianService
  ) {
    this.setForm();
  }

  get form(): FormGroup<TherapyJobForm> {
    return this.therapyJobForm;
  }

  public setForm(): void {
    const patientValidator = this.patientService ? PatientValidator.createValidator(this.patientService) : null;
    const physicianValidator = this.physicianService ? PhysicianValidator.createValidator(this.physicianService) : null;
    const defaultStartDate = getDefaultStartDate();

    this.therapyJobForm = new FormGroup<TherapyJobForm>({
      patient: new FormControl(null, [Validators.required], patientValidator || []),
      volume: new FormControl(null, [
        Validators.required,
        PositiveNumberValidator.isPositive(),
        Validators.max(MAX_VALUE_VOLUME),
        DataTypeValidator.isNumber(),
        DecimalPrecisionValidator.maxDecimals(MAX_DECIMALS_VOLUME)
      ]),
      flowRate: new FormControl(null, [
        Validators.required,
        PositiveNumberValidator.isPositive(),
        Validators.max(MAX_VALUE_FLOW_RATE),
        DataTypeValidator.isNumber(),
        DecimalPrecisionValidator.isInteger()
      ]),
      flowRateUnit: new FormControl(null, [DataTypeValidator.isString()]),
      startDate: new FormControl(defaultStartDate, [Validators.required]),
      hours: new FormControl(getTimeFromDate(defaultStartDate), [Validators.required]),
      physician: new FormControl(null, [Validators.required], physicianValidator || []),
      annotations: new FormControl(null, Validators.maxLength(ANNOTATION_MAX_LENGTH)),
      medication: new FormGroup({
        medicines: new FormArray([
          new FormGroup({
            medicine: new FormControl(null),
            customAmount: new FormControl(null),
            unit: new FormControl(null, [Validators.required, MedicineUnitValidator.unitNotExist()]),
            text: new FormControl(null, [Validators.required])
          })
        ]),
        carrier: new FormControl(null, [Validators.required])
      }),
      prepared: new FormControl(),
      prescriptionId: new FormControl(null),
      prescriptionObj: new FormControl(null)
    });

    // The time control's validity depends on the date control too, so a date change must re-run it
    this.therapyJobForm.controls.startDate.valueChanges.subscribe(() => {
      this.therapyJobForm.controls.hours.updateValueAndValidity({ onlySelf: true });
    });
  }

  public setFutureStartTimeRequired(required: boolean): void {
    const hoursControl = this.therapyJobForm.controls.hours;
    if (required) {
      hoursControl.addValidators(this.startTimeInFutureValidator);
    } else {
      hoursControl.removeValidators(this.startTimeInFutureValidator);
    }
    hoursControl.updateValueAndValidity();
  }

  public resetValidators(prescribedMedicine?: TherapyJobMedicine): void {
    const customAmountControl = (this.therapyJobForm.get('medication.medicines') as FormArray)
      ?.at(0)
      .get('customAmount');

    customAmountControl?.clearValidators();
    const validators = [
      Validators.required,
      PositiveNumberValidator.isPositive(),
      DataTypeValidator.isNumber(),
      DecimalPrecisionValidator.maxDecimals(MAX_DECIMALS_DOSE)
    ];

    // Manual entry (no prescribed medicine) caps at the default limit.
    // A prescribed medicine caps at its own amount. A lost/invalid prescribed
    // amount must BLOCK creation (handled upstream) — never fall back to MAX_VALUE_DOSE here.
    const maxDose = prescribedMedicine ? prescribedMedicine.amount : MAX_VALUE_DOSE;

    customAmountControl?.setValidators([...validators, Validators.max(maxDose)]);

    this.form.updateValueAndValidity();
  }

  public disable(listName: Array<string>) {
    listName.forEach((controlName) => {
      this.form.get(controlName).disable();
    });
  }

  public setDisabledForm(value: boolean) {
    this.disabledFormSignal.set(value);
  }
}
